/**
 * Massa de dados de DEMONSTRAÇÃO — coerente entre os 3 módulos (Pitada · Compasso ·
 * Elo). Limpa TUDO e popula um cenário rico o bastante para visualizar todas as
 * features: tendências, status, pendências da criança, efeito regulatório, painel de
 * responsabilidade, handoff entre disciplinas e timeline por alimento/por data.
 *
 * Só para desenvolvimento/demonstração — não é chamado em produção sem ação explícita.
 */
import { db } from "./schema.dexie";
import { CONFIG_PADRAO } from "./types";
import type {
  Alimento,
  Crianca,
  EspecialidadeProf,
  GrupoAlimentar,
  Oferta,
  PassoEscada,
  PropriedadesSensoriais,
  StatusAlimento,
} from "./types";
import type {
  EstadoRegulacao,
  ExperienciaMusical,
  FuncaoMusical,
  MomentoMusical,
  PerfilSonoro,
  StatusExperiencia,
  TipoExperiencia,
} from "./music/types";
import type { AnexoClinico, NotaSessao, Profissional } from "./elo/types";

export const DEMO_VERSION = "demo-2026-06-04-v1";
export const DEMO_FLAG = "autiprof-demo";

const DAY = 86400000;
const dias = (n: number) => new Date(Date.now() - n * DAY).toISOString();
const uid = () => crypto.randomUUID();

// ── Criança ──────────────────────────────────────────────────────────────────
const CRIANCA: Crianca = {
  id: "demo-crianca",
  apelido: "Téo",
  nome_completo: "Téo Almeida Souza",
  data_nascimento: "2019-03-14",
  foto: null,
  sistema_caa: "PECS",
  responsavel_nome: "Mariana Almeida Souza",
  responsavel_cpf: "123.456.789-09",
  recomendacoes:
    "Embalagem com divisórias; feijão sempre separado. Antecipar o cardápio com pictograma ~10 min antes da refeição.",
  criado_em: dias(70),
};

// ── Pitada: alimentos ────────────────────────────────────────────────────────
const SENS_VAZIO: PropriedadesSensoriais = { textura: null, temperatura: null, cor: null, forma_apresentacao: null, cheiro: null };
function alimento(
  id: string,
  nome: string,
  grupo: GrupoAlimentar,
  status: StatusAlimento,
  sensorial: Partial<PropriedadesSensoriais> = {},
  observacoes: string | null = null,
): Alimento {
  return {
    id,
    nome,
    fotos: [],
    marca_produto: null,
    categoria: grupo,
    grupo,
    sensorial: { ...SENS_VAZIO, ...sensorial },
    status,
    observacoes,
    arquivado: false,
    criado_em: dias(65),
  };
}

const ALIMENTOS: Alimento[] = [
  alimento("demo-brocolis", "brócolis", "fibra", "em_teste", { textura: "firme", cor: "verde", cheiro: "neutro" }, "trabalhado pela equipe (TO → fono)"),
  alimento("demo-morango", "morango", "fruta", "seguro", { textura: "macia", cor: "vermelha", cheiro: "doce" }, "favorito; aceita cortado ao meio"),
  alimento("demo-banana", "banana", "fruta", "seguro", { textura: "macia", cor: "amarela" }),
  alimento("demo-arroz", "arroz", "carboidrato", "seguro", { textura: "soltinha" }, "base das refeições"),
  alimento("demo-frango", "frango desfiado", "proteina", "seguro", { textura: "macia" }, "bem aceito sem tempero forte"),
  alimento("demo-cenoura", "cenoura", "fibra", "em_teste", { textura: "crocante", cor: "laranja" }),
  alimento("demo-feijao", "feijão", "proteina", "em_teste", { textura: "macia" }, "servir separado (recomendação)"),
  alimento("demo-iogurte", "iogurte", "laticinio", "seguro", { textura: "cremosa", temperatura: "gelada" }),
  alimento("demo-ervilha", "ervilha", "fibra", "recusado", { textura: "macia", cor: "verde" }, "sem pressão por enquanto"),
  alimento("demo-maca", "maçã", "fruta", "em_teste", { textura: "crocante" }),
  alimento("demo-tomate", "tomate", "fruta", "nao_oferecido", { cor: "vermelha" }),
];

// helpers de oferta
function oferta(
  alimentoId: string,
  passo: PassoEscada,
  diasAtras: number,
  over: Partial<Oferta> = {},
): Oferta {
  return {
    id: over.id ?? uid(),
    alimento_id: alimentoId,
    crianca_id: CRIANCA.id,
    data_hora: dias(diasAtras),
    passo_escada: passo,
    porcao_comida_pct: passo >= 8 ? 30 : null,
    porcao_oferecida: null,
    refeicao: "almoco",
    local: "cozinha",
    quem_ofereceu: "mãe",
    quem_presente: [],
    humor_antes: "tranquila",
    origem: "cuidador",
    confirmado: true,
    notas: null,
    ...over,
  };
}
const serie = (alimentoId: string, pares: [PassoEscada, number][], over: Partial<Oferta> = {}) =>
  pares.map(([p, d]) => oferta(alimentoId, p, d, over));

// ids das ofertas geradas pela terapia no brócolis (linkadas às notas do Elo)
const OF_BRO_2 = "demo-of-bro2", OF_BRO_3 = "demo-of-bro3", OF_BRO_4 = "demo-of-bro4", OF_BRO_5 = "demo-of-bro5";
const OF_MOR_7 = "demo-of-mor7", OF_CEN_2 = "demo-of-cen2";

const OFERTAS: Oferta[] = [
  // brócolis: a subida conduzida pela terapia (TO 2→4, fono 5) + casa no meio
  oferta("demo-brocolis", 0, 52, { quem_ofereceu: "mãe" }),
  oferta("demo-brocolis", 1, 46),
  oferta("demo-brocolis", 2, 40, { id: OF_BRO_2, origem: "profissional", profissional_nome: "Marta Lima", profissional_especialidade: "to", notas: "sessão TO" }),
  oferta("demo-brocolis", 2, 33, { local: "casa" }),
  oferta("demo-brocolis", 3, 25, { id: OF_BRO_3, origem: "profissional", profissional_nome: "Marta Lima", profissional_especialidade: "to", notas: "sessão TO" }),
  oferta("demo-brocolis", 3, 18, { local: "casa" }),
  oferta("demo-brocolis", 4, 12, { id: OF_BRO_4, origem: "profissional", profissional_nome: "Marta Lima", profissional_especialidade: "to", notas: "sessão TO" }),
  oferta("demo-brocolis", 5, 5, { id: OF_BRO_5, origem: "profissional", profissional_nome: "Ana Souza", profissional_especialidade: "fono", notas: "sessão fono" }),
  // morango: seguro, alto
  ...serie("demo-morango", [[5, 40], [6, 30], [7, 20], [8, 8], [9, 2]]),
  oferta("demo-morango", 7, 14, { id: OF_MOR_7, origem: "profissional", profissional_nome: "Ana Souza", profissional_especialidade: "fono", notas: "mastigação" }),
  // banana / arroz / frango / iogurte: seguros, consistentes
  ...serie("demo-banana", [[8, 35], [9, 20], [9, 6]]),
  ...serie("demo-arroz", [[8, 30], [9, 15], [9, 4]]),
  ...serie("demo-frango", [[7, 28], [8, 16], [9, 5]]),
  ...serie("demo-iogurte", [[8, 22], [9, 9]]),
  // cenoura: em teste, oscilando (TO começou)
  oferta("demo-cenoura", 1, 30),
  oferta("demo-cenoura", 2, 22, { id: OF_CEN_2, origem: "profissional", profissional_nome: "Marta Lima", profissional_especialidade: "to", notas: "sessão TO" }),
  oferta("demo-cenoura", 2, 14),
  oferta("demo-cenoura", 1, 6),
  // feijão / maçã: em teste, primeiras explorações
  ...serie("demo-feijao", [[1, 18], [2, 9], [3, 3]]),
  ...serie("demo-maca", [[2, 12], [3, 4]]),
  // ervilha: recusada
  ...serie("demo-ervilha", [[0, 24], [1, 16], [0, 7]]),
  // pendentes da criança (modo escolha) — aparecem em "confirmar"
  oferta("demo-morango", 8, 1, { origem: "crianca", confirmado: false, quem_ofereceu: null, humor_antes: null, notas: "escolheu no modo escolha", refeicao: "lanche" }),
  oferta("demo-banana", 8, 0, { origem: "crianca", confirmado: false, quem_ofereceu: null, humor_antes: null, notas: "escolheu no modo escolha", refeicao: "lanche" }),
];

// ── Compasso: experiências + momentos ─────────────────────────────────────────
function sonoro(p: Partial<PerfilSonoro>): PerfilSonoro {
  return { andamento: null, intensidade: null, timbre: null, previsibilidade: null, familiaridade: null, ...p };
}
function experiencia(
  id: string,
  nome: string,
  tipo: TipoExperiencia,
  funcao: FuncaoMusical,
  status: StatusExperiencia,
  modalidade: ExperienciaMusical["modalidade"],
  son: Partial<PerfilSonoro> = {},
): ExperienciaMusical {
  return {
    id, nome, tipo, midias: [], funcao, sonoro: sonoro(son), modalidade, status,
    observacoes: null, arquivado: false, criado_em: dias(60),
  };
}

const EXPERIENCIAS: ExperienciaMusical[] = [
  experiencia("demo-banho", "playlist do banho", "playlist", "regular", "favorita", ["ouvir"], { andamento: "lento", intensidade: "suave", previsibilidade: "alta", familiaridade: "favorita" }),
  experiencia("demo-soneca", "playlist da soneca", "playlist", "regular", "favorita", ["ouvir"], { andamento: "lento", intensidade: "suave", familiaridade: "favorita" }),
  experiencia("demo-oi", "canção do oi", "cancao", "conectar", "favorita", ["ouvir", "cantar"], { andamento: "moderado", previsibilidade: "alta", familiaridade: "conhecida" }),
  experiencia("demo-tambor", "tambor", "instrumento", "ativar", "em_exploracao", ["tocar", "mover"], { intensidade: "forte", timbre: "percussivo" }),
  experiencia("demo-chocalho", "chocalho", "instrumento", "conectar", "em_exploracao", ["tocar"], { intensidade: "media" }),
  experiencia("demo-danca", "dança das músicas", "atividade", "motor", "em_exploracao", ["mover", "ouvir"], { andamento: "rapido", intensidade: "forte" }),
  experiencia("demo-improviso", "improviso com a voz", "improviso", "comunicar", "sensivel", ["cantar"], { previsibilidade: "baixa", familiaridade: "nova" }),
];

function momento(
  expId: string,
  passo: number,
  diasAtras: number,
  antes: EstadoRegulacao | null = null,
  depois: EstadoRegulacao | null = null,
  over: Partial<MomentoMusical> = {},
): MomentoMusical {
  return {
    id: uid(),
    experiencia_id: expId,
    crianca_id: CRIANCA.id,
    data_hora: dias(diasAtras),
    passo_engajamento: passo as MomentoMusical["passo_engajamento"],
    estado_antes: antes,
    estado_depois: depois,
    duracao_min: 10,
    contexto: null,
    com_quem: [],
    origem: "cuidador",
    confirmado: true,
    notas: null,
    ...over,
  };
}

const MOMENTOS: MomentoMusical[] = [
  // banho: tende a ACALMAR (cima → regulado), engajamento subindo
  momento("demo-banho", 3, 40, "desregulado_cima", "regulado"),
  momento("demo-banho", 4, 30, "desregulado_cima", "regulado"),
  momento("demo-banho", 5, 18, "desregulado_cima", "regulado"),
  momento("demo-banho", 6, 6, "regulado", "regulado"),
  // soneca: ACALMA
  momento("demo-soneca", 2, 28, "desregulado_cima", "regulado"),
  momento("demo-soneca", 3, 16, "desregulado_cima", "desregulado_baixo"),
  momento("demo-soneca", 3, 5, "desregulado_cima", "regulado"),
  // canção do oi: engajamento alto, conexão
  momento("demo-oi", 5, 35, "desregulado_baixo", "regulado"),
  momento("demo-oi", 6, 20, "regulado", "regulado"),
  momento("demo-oi", 7, 7, "regulado", "regulado"),
  // tambor: tende a ATIVAR (baixo → cima), subindo
  momento("demo-tambor", 4, 26, "desregulado_baixo", "regulado"),
  momento("demo-tambor", 5, 15, "desregulado_baixo", "desregulado_cima"),
  momento("demo-tambor", 6, 4, "regulado", "desregulado_cima"),
  // dança: ATIVA
  momento("demo-danca", 5, 22, "desregulado_baixo", "desregulado_cima"),
  momento("demo-danca", 6, 9, "regulado", "desregulado_cima"),
  momento("demo-danca", 6, 2, "desregulado_baixo", "desregulado_cima"),
  // chocalho: exploração
  momento("demo-chocalho", 3, 19, "regulado", "regulado"),
  momento("demo-chocalho", 4, 8, "regulado", "regulado"),
  // improviso: SENSÍVEL — desregula (regulado → cima), engajamento baixo
  momento("demo-improviso", 1, 21, "regulado", "desregulado_cima"),
  momento("demo-improviso", 0, 12, "regulado", "desregulado_cima"),
  momento("demo-improviso", 1, 5, "regulado", "desregulado_cima"),
  // pendentes da criança (modo escolha musical)
  momento("demo-tambor", 4, 1, null, null, { origem: "crianca", confirmado: false, notas: "escolheu no modo escolha", duracao_min: null }),
  momento("demo-banho", 4, 0, null, null, { origem: "crianca", confirmado: false, notas: "escolheu no modo escolha", duracao_min: null }),
];

// ── Elo: profissionais + notas ────────────────────────────────────────────────
function profissional(
  id: string,
  nome: string,
  especialidade: EspecialidadeProf,
  registro: string,
): Profissional {
  return { id, nome, especialidade, registro_classe: registro, contato: null, arquivado: false, criado_em: dias(60) };
}
const PROFISSIONAIS: Profissional[] = [
  profissional("demo-prof-ana", "Ana Souza", "fono", "CRFa 1-12345"),
  profissional("demo-prof-marta", "Marta Lima", "to", "CREFITO 123456-F"),
  profissional("demo-prof-joao", "João Pereira", "psico", "CRP 06/12345"),
  profissional("demo-prof-carla", "Carla Nunes", "nutri", "CRN 12345"),
];

const ANEXO_FOTO_ID = "demo-foto-anexo";
const ANEXO_SENSIVEL: AnexoClinico[] = [{ blob_id: ANEXO_FOTO_ID, tipo: "foto", sensibilidade: "restrito_anel" }];

function nota(
  profId: string,
  esp: EspecialidadeProf,
  diasAtras: number,
  objetivo: string,
  over: Partial<NotaSessao> = {},
): NotaSessao {
  return {
    id: uid(),
    crianca_id: CRIANCA.id,
    profissional_id: profId,
    especialidade: esp,
    data_sessao: dias(diasAtras),
    objetivo,
    alimento_id: null,
    degrau_alvo: null,
    degrau_observado: null,
    atividades: [],
    resultados: null,
    orientacoes_casa: null,
    orientacao_feita_em: null,
    observacoes: null,
    anexos: [],
    oferta_id_gerada: null,
    estado: "publicada",
    versao_de: null,
    criado_em: dias(diasAtras),
    ...over,
  };
}

const NOTAS: NotaSessao[] = [
  // brócolis: handoff TO (2→4) → fono (5)
  nota("demo-prof-marta", "to", 40, "tolerar brócolis no prato", {
    alimento_id: "demo-brocolis", degrau_alvo: 2, degrau_observado: 2, oferta_id_gerada: OF_BRO_2,
    atividades: ["dessensibilização sensorial", "exposição no prato"],
    orientacoes_casa: "servir junto, sem cobrança; não insistir.",
    orientacao_feita_em: dias(36), // já aplicada em casa (demo do toggle)
  }),
  nota("demo-prof-marta", "to", 25, "tocar o brócolis", {
    alimento_id: "demo-brocolis", degrau_alvo: 3, degrau_observado: 3, oferta_id_gerada: OF_BRO_3,
    atividades: ["brincadeira com textura"], orientacoes_casa: "deixar tocar com a mão na hora do preparo.",
  }),
  nota("demo-prof-marta", "to", 12, "cheirar o brócolis", {
    alimento_id: "demo-brocolis", degrau_alvo: 4, degrau_observado: 4, oferta_id_gerada: OF_BRO_4,
    orientacoes_casa: "aproximar do nariz, sem obrigar a provar.",
  }),
  nota("demo-prof-ana", "fono", 5, "levar o brócolis à boca", {
    alimento_id: "demo-brocolis", degrau_alvo: 6, degrau_observado: 5, oferta_id_gerada: OF_BRO_5,
    atividades: ["exploração oral", "lamber a ponta"],
    observacoes: "textura já tolerada pela TO — pude avançar. Obrigada @TO!",
    orientacoes_casa: "oferecer um pedacinho na ponta da colher.",
    anexos: ANEXO_SENSIVEL,
  }),
  // morango (fono — mastigação), cenoura (TO)
  nota("demo-prof-ana", "fono", 14, "mastigação de sólido macio", {
    alimento_id: "demo-morango", degrau_alvo: 8, degrau_observado: 7, oferta_id_gerada: OF_MOR_7,
    orientacoes_casa: "cortar ao meio; incentivar mastigar dos dois lados.",
  }),
  nota("demo-prof-marta", "to", 22, "tolerar cenoura no prato", {
    alimento_id: "demo-cenoura", degrau_alvo: 3, degrau_observado: 2, oferta_id_gerada: OF_CEN_2,
    orientacoes_casa: "apresentar em palitos, sem cobrança.",
  }),
  // psico (geral — sem alimento)
  nota("demo-prof-joao", "psico", 8, "reduzir ansiedade na hora do almoço", {
    atividades: ["respiração", "antecipação com pictograma"],
    observacoes: "ansiedade antecipatória alta; rigidez com pratos misturados.",
    orientacoes_casa: "antecipar o cardápio com pictograma ~10 min antes; manter rotina previsível.",
  }),
  // nutri (geral — sem alimento)
  nota("demo-prof-carla", "nutri", 16, "priorizar fibras e proteína na semana", {
    observacoes: "ingestão de fibras baixa; brócolis e cenoura são bons alvos agora.",
    orientacoes_casa: "oferecer brócolis 2x/semana junto do arroz (seguro).",
  }),
];

/** Limpa TODAS as tabelas e popula a massa de demonstração. */
export async function semearDemo(): Promise<void> {
  // anexo "foto" clínica fake (Blob), referenciado pela nota sensível
  const fotoAnexo = { id: ANEXO_FOTO_ID, blob: new Blob(["demo"], { type: "image/png" }), criado_em: dias(5) };

  await db.transaction(
    "rw",
    [db.crianca, db.alimento, db.oferta, db.experiencia, db.momento, db.profissional, db.nota, db.foto, db.config],
    async () => {
      await Promise.all([
        db.crianca.clear(), db.alimento.clear(), db.oferta.clear(),
        db.experiencia.clear(), db.momento.clear(), db.profissional.clear(),
        db.nota.clear(), db.foto.clear(), db.config.clear(),
      ]);
      await db.crianca.bulkAdd([CRIANCA]);
      await db.alimento.bulkAdd(ALIMENTOS);
      await db.oferta.bulkAdd(OFERTAS);
      await db.experiencia.bulkAdd(EXPERIENCIAS);
      await db.momento.bulkAdd(MOMENTOS);
      await db.profissional.bulkAdd(PROFISSIONAIS);
      await db.nota.bulkAdd(NOTAS);
      await db.foto.bulkAdd([fotoAnexo]);
      await db.config.put({ id: "singleton", ...CONFIG_PADRAO, crianca_ativa_id: CRIANCA.id });
    },
  );
}
